// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconWrapper } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import ArrowNavigation from "@sc/App/Components/XngCarousel/ArrowNavigation";
import { xSwiperStyle } from "@sc/App/Components/XngCarousel/styles";
import { color } from "@sc/XDL/Utilities";
import { mergeWithCss } from "@sc/XDL/Utilities/mergeWithCss";
import pick from "lodash/pick";
import React, { FC, isValidElement, useEffect, useState } from "react";
import { css } from "styled-components";
import SwiperCore, { A11y, Autoplay, Controller, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay]);

export type Props = SwiperProps & {
    extendedStyles?: SwiperStyle;
    autoPlay?: boolean;
};

export interface SwiperStyle {
    container?: InjectableCss;
}

export const swiperButtonStyle = css`
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    width: fit-content;
    padding: 8px;
    background-color: ${color("common.ui7", 0.66)};
    ${IconWrapper} {
        color: ${color("common.ui1")};
    }
    &:hover {
        opacity: 0.66;
    }
    &.swiper-button-disabled {
        display: none;
    }
`;
// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}
            .swiper {
                position: relative;
                .xng-carousel-prev {
                    left: 0;
                    ${swiperButtonStyle};
                }
                .xng-carousel-next {
                    right: 0;
                    ${swiperButtonStyle}
                }
            }
            .xng-pagination-container {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 16px;

                & > div,
                & > button {
                    margin: 0;
                }
            }

            .xng-pagination-container > .swiper-scrollbar.swiper-scrollbar-horizontal {
                height: 4px;
                width: 100%;
                position: relative;
                top: unset;
                right: unset;
                bottom: unset;
                left: unset;
                transform: initial;
                background: ${color("common.ui2")};
                margin-top: 16px;
                & > .swiper-scrollbar-drag {
                    background: ${color("common.ui9")};
                    border-radius: 4px;
                }
            }

            .swiper-pagination {
                position: relative;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                gap: 8px 4px;

                &.swiper-pagination-lock {
                    display: none;
                }

                .swiper-pagination-bullet {
                    width: 10px;
                    height: 10px;
                    margin: 0;
                    background: ${color("common.white")};
                    border: 1px solid ${color("common.ui4")};
                    border-radius: 0;
                    opacity: 1;
                    &:hover:not(.swiper-pagination-bullet-active) {
                        background: ${color("common.ui8")};
                        border-color: ${color("common.ui8")};
                    }
                    &.swiper-pagination-bullet-active {
                        width: 10px;
                        height: 10px;
                        background: ${color("common.ui9")};
                        border: 1px solid ${color("common.ui9")};
                    }
                }
            }
        `,
    },
};

const XngCarousel: FC<Props> = props => {
    const {
        children,
        className,
        extendedStyles,
        slidesPerView = "auto",
        navigation = true,
        simulateTouch = false,
        spaceBetween = 20,
        pagination = false,
        scrollbar = { draggable: true, snapOnRelease: true },
        watchOverflow = true,
        updateOnWindowResize = true,
        centerInsufficientSlides = true,
        draggable = false,
        freeMode = false,
        ...swiperProps
    } = props;
    const [isSwiperCssImported, setIsSwiperCssImported] = useState(false);
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));

    useEffect(() => {
        const getStyle = async () => {
            /* eslint-disable global-require */
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            await require("swiper/swiper-bundle.css");
            /* eslint-enable global-require */
        };
        getStyle().then(_ => setIsSwiperCssImported(true));
    }, []);

    if (!isSwiperCssImported) {
        return null;
    }

    let paginationOptions;
    let scrollbarOptions;

    if (pagination) {
        paginationOptions =
            typeof pagination === "boolean" ? { el: ".swiper-pagination", clickable: true } : pagination;
    }

    if (scrollbar) {
        scrollbarOptions = typeof scrollbar === "boolean" ? { draggable: true, el: ".swiper-scrollbar" } : scrollbar;
    }

    const isVertical = swiperProps.direction === "vertical";

    return (
        <StyledWrapper
            {...styles.container}
            className={`carousel-main-wrapper ${className || ""}`}
            {...{ autoPlay: props.autoPlay }}
        >
            <Swiper
                {...swiperProps}
                pagination={paginationOptions}
                scrollbar={scrollbarOptions}
                slidesPerView={slidesPerView}
                navigation={navigation}
                simulateTouch={simulateTouch}
                spaceBetween={spaceBetween}
                watchOverflow={watchOverflow}
                updateOnWindowResize={updateOnWindowResize}
                centerInsufficientSlides={centerInsufficientSlides}
                draggable={draggable}
                freeMode={freeMode}
                observeParents
                observer
            >
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props as {}, [...swiperSlidePropsKeys] as never);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                        </SwiperSlide>
                    );
                })}

                {(pagination || navigation || scrollbar) && (
                    <StyledWrapper className="xng-pagination-container">
                        {navigation && <ArrowNavigation arrowType="prev" showOnMobile={true} />}
                        {pagination && <div className="swiper-pagination" />}
                        {scrollbar && <div className="swiper-scrollbar" />}
                        {navigation && <ArrowNavigation arrowType="next" showOnMobile={true} />}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

export default XngCarousel;
